// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useMemo, useEffect, useRef } from "react";
import { Search, ChevronDown, X } from "lucide-react";

const MultiSelectFilter = ({
  label,
  options,
  selectedValues,
  onSelectionChange,
  searchPlaceholder = "Search",
  isOpen,
  onToggle,
  filterKey,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Reset search term when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (isOpen) {
          onToggle(filterKey);
        }
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onToggle, filterKey]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [options, searchTerm]);

  const handleToggleOption = (value) => {
    const newSelection = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onSelectionChange(newSelection);
  };

  const handleClearAll = () => {
    onSelectionChange([]);
  };

  const handleToggle = () => {
    onToggle(filterKey);
  };

  const selectedCount = selectedValues.length;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={handleToggle}
        className={`w-full px-3 py-2 text-left rounded-md flex items-center justify-between border ${
          selectedCount > 0
            ? "bg-gradient-primary-secondary-tint border-primary-color primary-color"
            : "border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-700"
        }`}
      >
        <span
          className={`text-sm font-bold ${selectedCount > 0 ? "primary-color" : ""}`}
        >
          {selectedCount > 0 ? `(${selectedCount}) ${label}` : label}
        </span>
        <div className="flex items-center space-x-1">
          <ChevronDown
            className={`w-4 h-4 font-bold transition-transform ${selectedCount > 0 || isOpen ? "primary-color" : "text-gray-500 dark:text-slate-400"} ${isOpen ? "rotate-180" : ""}`}
          />
          {selectedCount > 0 && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
              className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 cursor-pointer"
            >
              <X className="w-4 h-4" />
            </span>
          )}
        </div>
      </button>

      {isOpen && (
        <div className="absolute min-w-60 top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg z-50 max-h-96 overflow-hidden">
          {/* Search bar */}
          <div className="p-3 border-b border-gray-200 dark:border-slate-600">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-slate-500 w-4 h-4" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent dark:placeholder-slate-400"
              />
            </div>
          </div>

          {/* Options list */}
          <div
            className="max-h-80 overflow-y-auto"
            style={{ scrollbarWidth: "thin" }}
          >
            {filteredOptions.map((option) => (
              <label
                key={option.value}
                className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => handleToggleOption(option.value)}
                  className="w-4 h-4 border-gray-300 dark:border-slate-500 rounded dark:bg-slate-700 accent-[var(--primary-color)] focus:ring-2 focus:ring-[var(--primary-color)]"
                />
                <span className="ml-3 text-sm text-gray-700 dark:text-slate-200">
                  {option.label}
                </span>
              </label>
            ))}
            {filteredOptions.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-slate-400">
                No options found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectFilter;
